import { tokenManager } from '@/lib/tokenManager'
import { ApiError, api } from '@/lib/api'
import type {
  CheckDuplicatesResponse,
  ConfirmUploadPayload,
  ExtractedTable,
  MatchBatchResponse,
  NormalizeDescriptionBatchResponse,
  PredictBatchResponse,
  PredictCoaBatchResponse,
  StatementByFileRow,
  StatusResponse,
  UploadResponse,
} from './types'

/**
 * Upload the file via multipart/form-data. Bypasses the JSON transport in
 * `api.ts` but still picks up the Bearer token from tokenManager.
 *
 * Returns two-shape response: 200 with tables (sync) or 202 with queued
 * status (async). The caller should inspect the HTTP status.
 */
export async function uploadStatementFile(params: {
  file: File
  statement_type: string
  period: string
  account_no?: string
  bank_account_id?: number
  signal?: AbortSignal
}): Promise<{ response: UploadResponse; status: number }> {
  const fd = new FormData()
  fd.append('file', params.file)
  fd.append('statement_type', params.statement_type)
  fd.append('period', params.period)
  if (params.account_no) fd.append('account_no', params.account_no)
  if (params.bank_account_id != null) fd.append('bank_account_id', String(params.bank_account_id))
  const token = tokenManager.get()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch('/api/statements/upload', {
    method: 'POST',
    headers,
    body: fd,
    signal: params.signal,
  })
  const ct = res.headers.get('content-type') || ''
  let body: unknown = null
  if (ct.includes('application/json')) {
    try {
      body = await res.json()
    } catch {
      /* ignore */
    }
  }
  if (res.status === 402) {
    throw new ApiError('Transaction limit reached', 402, body)
  }
  if (!res.ok) {
    const msg =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Upload failed (${res.status})`
    throw new ApiError(msg, res.status, body)
  }
  return { response: body as UploadResponse, status: res.status }
}

export const uploadApi = {
  status: (s3FileId: number) => api.get<StatusResponse>(`/api/statements/status/${s3FileId}`),
  checkDuplicates: (tables: ExtractedTable[]) =>
    api.post<CheckDuplicatesResponse>('/api/statements/check-duplicates', { tables }),
  matchBatch: (descriptions: string[]) =>
    api.post<MatchBatchResponse>('/api/statements/match-batch', { descriptions }),
  predictBatch: (descriptions: string[]) =>
    api.post<PredictBatchResponse>('/api/statements/predict-batch', { descriptions }),
  predictCoaBatch: (transactions: { description: string; category?: string }[]) =>
    api.post<PredictCoaBatchResponse>('/api/statements/predict-coa-batch', { transactions }),
  normalizeDescriptionBatch: (descriptions: string[]) =>
    api.post<NormalizeDescriptionBatchResponse>('/api/statements/normalize-description-batch', { descriptions }),
  confirmUpload: (payload: ConfirmUploadPayload) =>
    api.post<{ message?: string }>('/api/statements/confirm-upload', payload),
  byFile: (s3FileId: number) =>
    api.get<StatementByFileRow[]>(`/api/statements/by-file/${s3FileId}`),
}
